import React from 'react';
import classNames from 'classnames/bind';
import {
  Button,
  CategoryTreeFacet,
  PriceFilterFacet,
  SortDropdown
} from '@components';
import { Icon, Select, Skeleton } from 'gd-design-library';
import { Category, ProductsResponse } from '@services';

import styles from './ProductListingPageHeader.module.scss';

const CN = 'product-listing-page-header';
const filtersClassName = `${CN}--filters`;
const selectFilterClassName = `${CN}--select-filter`;

const cx = classNames.bind(styles);

interface FilterConfig {
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
  showButton?: boolean;
  selectedTerms?: Record<string, string[]>;
  deleteTerm?: (facet: string, term: string) => void;
  selectTerm?: (facet: string, term: string) => void;
  facetLabelsByName?: Record<string, string | undefined>;
  additionalFilters?: React.ReactNode;
}

interface CategoryConfig {
  current?: Category | null;
  showTreeFacet?: boolean;
}

interface SortConfig {
  value?: string;
  onChange?: (value: string) => void;
}

interface FiltersProps {
  isLoading?: boolean;
  isFacetDataLoading?: boolean;
  isMobileOrTablet?: boolean;
  shouldShowFilters?: boolean;
  filterConfig?: FilterConfig;
  categoryConfig?: CategoryConfig;
  sortConfig?: SortConfig;
  facets?: ProductsResponse['facets'];
  productsInfo?: ProductsResponse | null;
}

export const Filters: React.FC<FiltersProps> = ({
  isLoading = false,
  isFacetDataLoading = false,
  isMobileOrTablet = false,
  shouldShowFilters = false,
  filterConfig = {},
  categoryConfig = {},
  sortConfig = {},
  facets,
  productsInfo
}) => {
  const priceFacetName = process.env.NEXT_PUBLIC_SEARCH_ENGINE === 'mxp' ? 'price.centAmount' : 'price';

  if (isLoading && !isMobileOrTablet) {
    return <Skeleton width='280px' height='24px' />;
  }

  if (isLoading && isMobileOrTablet) {
    return null;
  }

  if (!shouldShowFilters) {
    return null;
  }

  return (
    <div className={ cx(filtersClassName) }>
      {filterConfig.showButton && filterConfig.setIsOpen && (
        <Button
          variant='inherit'
          onClick={ (): void => filterConfig.setIsOpen?.(!filterConfig.isOpen) }
          iconEnd={ <Icon name='filter' width={ 24 } height={ 24 } /> }
        >
          Filters
        </Button>
      )}
      {categoryConfig.showTreeFacet && (
        <Select
          initiator={
            <Button variant='inherit' iconEnd={ <Icon name='arrowDown' width={ 24 } height={ 24 } /> }>
              Category
            </Button>
          }
        >
          <div className={ cx(selectFilterClassName) }>
            <CategoryTreeFacet
              isLoading={ isFacetDataLoading }
              label='Category'
              activeCategory={ categoryConfig.current }
              childCategories={ categoryConfig.current?.children ?? [] }
              withAccordion={ false }
            />
          </div>
        </Select>
      )}
      {filterConfig.showButton && (
        <Select
          initiator={
            <Button variant='inherit' iconEnd={ <Icon name='arrowDown' width={ 24 } height={ 24 } /> }>
              Price
            </Button>
          }
        >
          <div className={ cx(selectFilterClassName) }>
            <PriceFilterFacet
              facets={ facets || productsInfo?.facets }
              facetLabelsByName={ filterConfig.facetLabelsByName }
              isAWSEnv={ process.env.NEXT_PUBLIC_SEARCH_ENGINE === 'mxp' }
              facet={ priceFacetName }
              selectedTerms={ filterConfig.selectedTerms?.[priceFacetName] ?? [] }
              selectTerm={ (term): void => filterConfig.selectTerm?.(priceFacetName, term) }
              deleteTerm={ (term): void => filterConfig.deleteTerm?.(priceFacetName, term) }
              isLoading={ isLoading }
              withAccordion={ false }
            />
          </div>
        </Select>
      )}
      {sortConfig.value !== undefined && sortConfig.onChange && (
        <SortDropdown
          value={ sortConfig.value }
          onChange={ sortConfig.onChange }
        />
      )}
      {filterConfig.additionalFilters}
    </div>
  );
};

export default Filters;
